/**
 * Price Formatting Utilities
 *
 * Provides locale-aware price formatting to ensure customers see
 * prices in their expected format (e.g., 1,234.56 for US, 1.234,56 for EU).
 *
 * Uses currency CODES (USD, CHF, EUR) rather than symbols, with locale-aware
 * positioning (e.g., "USD 10,000" for US, "10,000 CHF" for Switzerland).
 */

import { getIntlLocale } from '@/lib/utils/locale';

/** Re-export for backwards compatibility — prefer `@/lib/utils/locale`. */
export { getIntlLocale };

/**
 * Maps currency codes to their home locale for formatting.
 *
 * Currency code position (before vs. after the amount) follows the currency's
 * home locale, not the user's browsing locale. This ensures EUR always
 * formats as "10.000 EUR" regardless of whether the user is on us-en or de-de.
 *
 * Without this, a us-en user would see "EUR 9,400" (American convention)
 * instead of the correct European "9.400 EUR".
 */
const CURRENCY_HOME_LOCALE: Record<string, string> = {
  EUR: 'de-DE', // "10.000 EUR"
  CHF: 'fr-CH', // "20 500 CHF"
  GBP: 'en-GB', // "£10,000"
  USD: 'en-US', // "USD 10,000"
  JPY: 'ja-JP', // "10,000 JPY"
  HKD: 'zh-Hant-HK',
  SGD: 'en-SG',
  AUD: 'en-AU',
  NZD: 'en-NZ',
  SEK: 'sv-SE',
  DKK: 'da-DK',
  NOK: 'nb-NO',
  PLN: 'pl-PL',
  CZK: 'cs-CZ',
  AED: 'ar-AE',
  SAR: 'ar-SA',
  QAR: 'ar-QA',
  KWD: 'ar-KW',
  BHD: 'ar-BH',
  KRW: 'ko-KR',
  MXN: 'es-MX',
  BRL: 'pt-BR',
  CAD: 'en-CA',
};

/**
 * Maps country codes to the preferred locale for CURRENCY formatting.
 * This determines whether currency code appears before or after the number,
 * and what thousand/decimal separators to use.
 *
 * This is separate from language locale because currency formatting follows
 * regional conventions, not language preferences.
 *
 * Examples:
 * - Switzerland uses 'fr-CH' format: "20 500 CHF" (currency after, space separator)
 * - Germany uses 'de-DE' format: "10.000 EUR" (currency after, dot separator)
 * - USA uses 'en-US' format: "USD 10,000" (currency before, comma separator)
 */
const CURRENCY_FORMAT_LOCALE: Record<string, string> = {
  // Americas - currency before
  us: 'en-US',
  ca: 'en-CA',
  mx: 'es-MX',
  br: 'pt-BR',

  // UK - currency before (but uses symbol typically)
  gb: 'en-GB',

  // Europe - currency after (mostly)
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  es: 'es-ES',
  nl: 'nl-NL',
  be: 'fr-BE',
  at: 'de-AT',
  ch: 'fr-CH', // Swiss format: "20 500 CHF" - currency AFTER
  se: 'sv-SE',
  dk: 'da-DK',
  no: 'nb-NO',
  fi: 'fi-FI',
  ie: 'en-IE',
  pl: 'pl-PL',
  cz: 'cs-CZ',

  // Asia Pacific
  jp: 'ja-JP',
  kr: 'ko-KR',
  hk: 'zh-Hant-HK',
  tw: 'zh-Hant-TW',
  sg: 'en-SG',
  au: 'en-AU',
  nz: 'en-NZ',

  // Middle East
  ae: 'ar-AE',
  sa: 'ar-SA',
  qa: 'ar-QA',
  kw: 'ar-KW',
  bh: 'ar-BH',
};

/**
 * Normalize grouping separators to prevent cross-runtime inconsistencies.
 *
 * Intl.NumberFormat output for Swiss locales (fr-CH, de-CH) varies by runtime:
 *
 *   Node 18 (older CLDR)        → U+2019 RIGHT SINGLE QUOTATION MARK  → 10'000
 *   Node 22 (newer CLDR)        → U+0027 APOSTROPHE                   → 10'000
 *   Modern browsers (CLDR 44+)  → U+202F NARROW NO-BREAK SPACE        → 10 000
 *
 * Without normalization, SSR (Node) can produce "10'000" while the browser
 * hydration pass produces "10 000", causing a React hydration mismatch.
 *
 * We replace apostrophe-like separators with U+202F so the output is always
 * "10 000 CHF" regardless of the runtime.
 */
const normalizeGroupSeparator = (formatted: string): string =>
  formatted.replace(/[\u2019\u0027](?=\d)/g, '\u202F');

/**
 * Compute safe minimumFractionDigits / maximumFractionDigits defaults.
 *
 * Whole amounts get 0 decimals, fractional amounts get 2. Caller-supplied
 * options always win, but we clamp min ≤ max so Intl never throws a
 * RangeError when only one side is overridden.
 */
function resolveFractionDigits(
  amount: number,
  options?: Partial<Intl.NumberFormatOptions>
): { minimumFractionDigits: number; maximumFractionDigits: number } {
  const isWholeNumber = Number(amount) % 1 === 0;
  const defaultDigits = isWholeNumber ? 0 : 2;

  const max = options?.maximumFractionDigits ?? defaultDigits;
  const min = options?.minimumFractionDigits ?? defaultDigits;

  return {
    minimumFractionDigits: Math.min(min, max),
    maximumFractionDigits: max,
  };
}

/**
 * Get the preferred locale for currency formatting based on country code.
 * Extracts country from URL locale (e.g., 'ch-de' → 'ch' → 'fr-CH')
 */
function getCurrencyFormatLocale(urlLocale: string | undefined): string {
  if (!urlLocale) return 'en-US';
  const countryCode = urlLocale.split('-')[0].toLowerCase();
  return CURRENCY_FORMAT_LOCALE[countryCode] || getIntlLocale(urlLocale);
}

/**
 * Format a price according to the country's currency convention using currency CODES
 *
 * Uses currencyDisplay: 'code' to show USD, CHF, EUR etc. instead of symbols.
 * The country (extracted from locale) determines the formatting:
 * - US: "USD 10,000" (currency before, comma separator)
 * - Switzerland: "20 500 CHF" (currency after, space separator)
 * - Germany: "10.000 EUR" (currency after, dot separator)
 *
 * Special handling: USD always uses en-US locale to ensure consistent positioning.
 *
 * @param amount - The numeric price amount
 * @param currency - The currency code (e.g., 'USD', 'EUR', 'CHF')
 * @param locale - The URL locale (e.g., 'us-en', 'ch-de')
 * @param options - Additional Intl.NumberFormat options
 *
 * @example
 * formatPrice(10000, 'USD', 'us-en')  // "USD 10,000"
 * formatPrice(20500, 'CHF', 'ch-de')  // "20 500 CHF"
 * formatPrice(10000, 'EUR', 'de-de')  // "10.000 EUR"
 */
export function formatPrice(
  amount: number,
  currency: string,
  locale?: string,
  options?: Partial<Intl.NumberFormatOptions>
): string {
  // Use the currency's home locale to determine code position (e.g. EUR always
  // formats as "10.000 EUR" regardless of user locale). Fall back to the
  // country-derived locale for currencies not in the map.
  const intlLocale = CURRENCY_HOME_LOCALE[currency] ?? getCurrencyFormatLocale(locale);
  const fractionDigits = resolveFractionDigits(amount, options);

  try {
    const raw = new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      ...fractionDigits,
      ...options,
    }).format(amount);
    return normalizeGroupSeparator(raw);
  } catch {
    return (
      new Intl.NumberFormat(intlLocale, {
        ...fractionDigits,
        ...options,
      }).format(amount) + ` ${currency}`
    );
  }
}

/**
 * Format a price with currency SYMBOL (for compact display)
 *
 * @example
 * formatPriceWithSymbol(10000, 'USD', 'us-en')  // "$10,000"
 * formatPriceWithSymbol(10000, 'EUR', 'de-de')  // "10.000 €"
 */
export function formatPriceWithSymbol(
  amount: number,
  currency: string,
  locale?: string,
  options?: Partial<Intl.NumberFormatOptions>
): string {
  const intlLocale = getIntlLocale(locale);
  const fractionDigits = resolveFractionDigits(amount, options);

  try {
    const raw = new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      ...fractionDigits,
      ...options,
    }).format(amount);
    return normalizeGroupSeparator(raw);
  } catch {
    return (
      new Intl.NumberFormat(intlLocale, {
        ...fractionDigits,
        ...options,
      }).format(amount) + ` ${currency}`
    );
  }
}

/**
 * Format a price without currency symbol (just the number)
 *
 * @example
 * formatPriceNumber(1234.56, 'us-en') // "1,234.56"
 * formatPriceNumber(1234.56, 'de-de') // "1.234,56"
 */
export function formatPriceNumber(
  amount: number,
  locale?: string,
  options?: Partial<Intl.NumberFormatOptions>
): string {
  const intlLocale = getIntlLocale(locale);

  return new Intl.NumberFormat(intlLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(amount);
}

/**
 * Format a price with abbreviated notation for large amounts
 *
 * @example
 * formatPriceCompact(1234567, 'USD', 'us-en') // "$1.23M"
 */
export function formatPriceCompact(amount: number, currency: string, locale?: string): string {
  const intlLocale = getIntlLocale(locale);

  return new Intl.NumberFormat(intlLocale, {
    style: 'currency',
    currency,
    notation: 'compact',
    compactDisplay: 'short',
  }).format(amount);
}

/**
 * Structured price parts returned by formatPriceParts.
 *
 * Enables per-part rendering (e.g. styling the minus sign,
 * wrapping currency code in a <span>, strikethrough on amount only).
 */
export interface PriceParts {
  /** The full formatted string (same as formatPrice output) */
  formatted: string;
  /** Individual Intl.NumberFormat parts with minus-sign repositioned */
  parts: Intl.NumberFormatPart[];
}

/**
 * Format a price into structured parts for per-part JSX rendering.
 *
 * Uses formatToParts and repositions the minus sign so it appears
 * directly before the first digit (matching legacy Breitling behavior).
 *
 * @example
 * const { parts } = formatPriceParts(-1234.56, 'CHF', 'ch-de');
 * // parts can be mapped to JSX: <span key={i}>{part.value}</span>
 *
 * @example
 * const { formatted } = formatPriceParts(10000, 'EUR', 'de-de');
 * // formatted === "10.000 EUR"
 */
export function formatPriceParts(
  amount: number,
  currency: string,
  locale?: string,
  options?: Partial<Intl.NumberFormatOptions>
): PriceParts {
  const intlLocale = CURRENCY_HOME_LOCALE[currency] ?? getCurrencyFormatLocale(locale);
  const fractionDigits = resolveFractionDigits(amount, options);

  const formatter = new Intl.NumberFormat(intlLocale, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    ...fractionDigits,
    ...options,
  });

  const rawParts = formatter.formatToParts(amount);

  // Reposition minus sign: Intl may place it before the currency code
  // (e.g. "-CHF 1,234.56") but we want it before the first digit
  // (e.g. "CHF -1,234.56") to match legacy behavior.
  if (amount < 0) {
    const minusIdx = rawParts.findIndex((p) => p.type === 'minusSign');
    const firstDigitIdx = rawParts.findIndex(
      (p) => p.type === 'integer' || p.type === 'fraction' || p.type === 'decimal'
    );

    if (minusIdx !== -1 && firstDigitIdx !== -1 && minusIdx < firstDigitIdx - 1) {
      const [minus] = rawParts.splice(minusIdx, 1);
      const newDigitIdx = rawParts.findIndex(
        (p) => p.type === 'integer' || p.type === 'fraction' || p.type === 'decimal'
      );
      rawParts.splice(newDigitIdx, 0, minus);
    }
  }

  for (const part of rawParts) {
    if (part.type === 'group') {
      part.value = part.value.replace(/[\u2019\u0027]/g, '\u202F');
    }
  }

  return {
    formatted: rawParts.map((p) => p.value).join(''),
    parts: rawParts,
  };
}
